import { useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  Copy,
  Mail,
  PackageCheck,
} from "lucide-react";
import { toast } from "sonner";

const EASE = [0.16, 1, 0.3, 1];

const EMAIL = "m.a.brokerage1@gmail.com";

const PHONES = [
  {
    id: "primary",
    label: "DISPATCH DIRECT — 24/7",
    display: "+1 (805) 512-5109",
    href: "tel:+18055125109",
  },
  {
    id: "backup",
    label: "BACKUP DISPATCH LINE",
    display: "+1 (805) 415-5433",
    href: "tel:+18054155433",
  },
];

const CopyButton = ({ text, label, testid }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      data-testid={testid}
      onClick={copy}
      aria-label={`Copy ${label}`}
      className="flex h-11 w-11 shrink-0 items-center justify-center border border-[#383F56] text-slate-400 transition-colors duration-300 hover:border-[#FF5500] hover:text-[#FF5500]"
    >
      {copied ? (
        <Check className="h-4 w-4 text-[#10B981]" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </button>
  );
};

export default function Contact() {
  return (
    <section id="contact" data-testid="direct-contact-section" className="relative scroll-mt-20 border-t border-[#222736] py-28">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#F59E0B]">
            05 // DIRECT CONTACT
          </p>
          <h2 className="mt-4 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Talk To Dispatch
          </h2>
          <p className="mt-4 max-w-xl text-base leading-7 text-slate-300 lg:text-[17px] lg:leading-8">
            Carriers and shippers can call either dispatch line or email the
            team directly. Share your equipment and lanes, or your shipment
            origin, destination, weight, and timing.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="space-y-8"
          >
            {PHONES.map((phone) => (
              <div key={phone.id}>
                <p className="font-mono text-[10px] font-semibold tracking-[0.25em] text-slate-500">
                  {phone.label}
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <a
                    data-testid={phone.id === "primary" ? "contact-phone-link" : "contact-backup-phone-link"}
                    href={phone.href}
                    className="whitespace-nowrap font-display text-2xl font-black tracking-tight text-white transition-colors duration-300 hover:text-[#FF5500] sm:text-4xl"
                  >
                    {phone.display}
                  </a>
                  <CopyButton
                    text={phone.display}
                    label={phone.id === "primary" ? "Primary phone number" : "Backup phone number"}
                    testid={phone.id === "primary" ? "contact-phone-copy-button" : "contact-backup-phone-copy-button"}
                  />
                </div>
              </div>
            ))}

            <div>
              <p className="font-mono text-[10px] font-semibold tracking-[0.25em] text-slate-500">
                EMAIL DISPATCH
              </p>
              <div className="mt-3 flex items-center gap-4">
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${EMAIL}`}
                  className="inline-flex items-center gap-3 font-mono text-base font-semibold text-slate-200 transition-colors duration-300 hover:text-[#FF5500] sm:text-lg"
                >
                  <Mail className="h-5 w-5 text-[#FF5500]" />
                  {EMAIL}
                </a>
                <CopyButton
                  text={EMAIL}
                  label="Email address"
                  testid="contact-email-copy-button"
                />
              </div>
            </div>

          </motion.div>

          <motion.div
            data-testid="contact-status-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="relative overflow-hidden border border-[#222736] bg-[#12141C] p-8"
          >
            <div className="absolute right-0 top-0 h-1 w-full bg-gradient-to-r from-[#FF5500] to-[#F59E0B]" />
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] font-bold tracking-[0.25em] text-slate-400">
                DIRECT SUPPORT
              </span>
              <PackageCheck className="h-4 w-4 text-[#F59E0B]" />
            </div>

            <div className="mt-8 flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center border border-[#FF5500]/40 bg-[#FF5500]/10">
                <PackageCheck className="h-5 w-5 text-[#FF5500]" />
              </span>
              <span className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
                Freight Coordination
              </span>
            </div>

            <div className="mt-8 space-y-4 border-t border-[#222736] pt-6 font-mono text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">SHIPMENT MODES</span>
                <span className="text-right font-bold text-white">FTL · LTL · EXPEDITED</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">EQUIPMENT</span>
                <span className="text-right font-bold text-white">VAN · REEFER · FLATBED</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">COMMUNICATION</span>
                <span className="text-right text-slate-300">PICKUP THROUGH DELIVERY</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">OPERATING AUTHORITY</span>
                <span className="text-slate-300">MC #71370379</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">USDOT</span>
                <span className="text-slate-300">#8954053</span>
              </div>
            </div>

            <div className="mt-8 border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-4 py-3">
              <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-[#F59E0B]">
                WHEN READY, SHARE ORIGIN, DESTINATION, EQUIPMENT, WEIGHT, AND TIMING.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
